
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Circle, Download, Trash2, Clock, FileAudio, AudioWaveform } from 'lucide-react';
import { AudioRecording } from '@/hooks/recorder/useRecordingManager';
import { formatDuration } from '@/utils/formatTime';
import { Slider } from '@/components/ui/slider';

interface RecordingsTabContentProps {
  isRecording: boolean;
  onToggleRecording: () => void;
  recordings: AudioRecording[];
  onDownloadRecording: (recording: AudioRecording, filename: string) => void;
  onClearRecordings: () => void;
  onDeleteRecording?: (id: string) => void;
  elapsedTime: number;
  audioLevel?: number;
}

const RecordingsTabContent: React.FC<RecordingsTabContentProps> = ({
  isRecording,
  onToggleRecording,
  recordings,
  onDownloadRecording,
  onClearRecordings,
  onDeleteRecording,
  elapsedTime,
  audioLevel = 0
}) => {
  // Generate visualization bars based on time or audio level
  const generateVisualizationBars = (isPlaying: boolean = false) => {
    return [...Array(20)].map((_, i) => {
      let height;
      
      if (isRecording) {
        // When recording, use audio level or random
        height = Math.min(30 + Math.random() * (audioLevel || 30), 100);
      } else if (isPlaying) {
        // When playing a recording, generate more dynamic bars
        height = 20 + Math.random() * 80;
      } else {
        // Very small bars when not recording or playing
        height = 5 + Math.random() * 10;
      }
        
      return (
        <div 
          key={i} 
          className="h-full w-full bg-gradient-to-t from-neon-purple to-neon-blue rounded-sm" 
          style={{ 
            height: `${height}%`,
            opacity: (isRecording || isPlaying) ? Math.min(0.4 + height / 100, 1) : 0.3,
            transition: 'height 100ms ease-out',
            animation: (isRecording || isPlaying) ? `pulse-soft ${0.5 + Math.random()}s infinite` : 'none'
          }}
        />
      );
    });
  };

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 p-4 bg-gradient-to-b from-[#151520] to-[#0f0f1a] text-white">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white flex items-center gap-2">
            <AudioWaveform className="h-4 w-4 text-neon-purple" />
            <span className="drop-shadow-neon-text">Session Recordings</span>
          </h3>
          
          {recordings.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClearRecordings}
              className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              <span className="text-xs">Clear All</span>
            </Button>
          )}
        </div>
        
        {/* Playback volume control */}
        {recordings.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs text-white/60 flex items-center gap-1">
                Volume
              </label>
              <span className="text-xs text-white/60">80%</span>
            </div>
            <Slider
              value={[80]}
              min={0}
              max={100}
              step={1}
              className="h-2"
            />
          </div>
        )}
        
        {isRecording && (
          <Card className="p-3 bg-black/60 border border-red-500/40 shadow-neon-pink relative overflow-hidden backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Circle className="h-4 w-4 text-red-500 animate-pulse" />
                <span className="text-sm text-white">Recording in progress...</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 border border-[#403E43]/40">
                <Clock className="h-3 w-3 text-white/70" />
                <span className="text-xs text-white/90 font-mono">{formatDuration(elapsedTime)}</span>
              </div>
            </div>
            
            {/* Live visualizer */}
            <div className="h-10 bg-black/70 rounded-lg overflow-hidden flex items-end justify-center mt-3 border border-neon-blue/20 p-1">
              <div className="w-full h-full flex space-x-0.5">
                {generateVisualizationBars(true)}
              </div>
            </div>
            
            {/* Neon glow effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-transparent via-red-500/20 to-transparent animate-pulse-soft -z-10 rounded-lg"></div>
          </Card>
        )}
        
        <Button
          variant={isRecording ? "destructive" : "outline"}
          size="sm"
          onClick={onToggleRecording}
          className={`w-full relative overflow-hidden ${isRecording 
            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/50' 
            : 'bg-neon-purple/20 text-white hover:bg-neon-purple/30 border-neon-purple/50 shadow-neon-purple'}`}
        >
          <Circle className={`h-4 w-4 mr-2 ${isRecording ? 'text-red-500 animate-pulse' : ''}`} />
          <span>{isRecording ? 'Stop Recording' : 'Start Recording'}</span>
          
          {/* Animated shine effect for non-recording state */}
          {!isRecording && (
            <div className="absolute inset-0 w-full h-full animate-shine"></div>
          )}
        </Button>
        
        {recordings.length > 0 ? (
          <div className="space-y-2 mt-4">
            {recordings.map((recording, index) => (
              <div 
                key={recording.id || index} 
                className="p-3 rounded-md bg-gradient-to-r from-black/80 to-[#12121e]/80 hover:from-black/70 hover:to-[#1a1a2e]/70 transition-colors border border-neon-purple/20 hover:border-neon-purple/50 hover:shadow-neon group"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full flex items-center justify-center bg-neon-purple/20 text-neon-purple border border-neon-purple/40 hover:bg-neon-purple/40"
                      disabled
                    >
                      <Circle className="h-4 w-4" />
                    </Button>
                    <div className="flex flex-col">
                      <span className="text-sm text-white group-hover:text-neon-purple transition-colors">Recording {index + 1}</span>
                      <span className="text-xs text-white/60 font-mono">{formatDuration(recording.duration)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => onDownloadRecording(recording, `recording-${index + 1}.wav`)}
                      className="h-7 w-7 p-0 text-white hover:bg-neon-purple/30 hover:text-neon-purple rounded-full"
                      title="Download recording"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    
                    {onDeleteRecording && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onDeleteRecording(recording.id)}
                        className="h-7 w-7 p-0 text-white hover:bg-red-500/30 hover:text-red-400 rounded-full"
                        title="Delete recording"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Visualization indicator - static for now */}
                <div className="h-6 bg-black/70 rounded-lg overflow-hidden flex items-end justify-center mt-2 border border-neon-blue/30 p-1">
                  <div className="w-full h-full flex space-x-0.5">
                    {generateVisualizationBars(false)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !isRecording && (
            <div className="p-6 rounded-md bg-black/40 text-white/60 text-center text-sm border border-[#403E43]/40 mt-4 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-black/60 flex items-center justify-center border border-neon-purple/30">
                  <FileAudio className="h-8 w-8 text-neon-purple/40" />
                </div>
                <p className="text-white/80">No recordings yet</p>
                <p className="text-xs text-white/40 max-w-[200px]">Hit the record button to capture your session audio</p>
              </div>
            </div>
          )
        )}
      </div>
    </ScrollArea>
  );
};

export default RecordingsTabContent;
